import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/SEO";
import ToolShell, { Field, inputCls } from "../../components/ToolShell";
import { api } from "../../lib/api";

export default function LoveCalculator() {
  const [name1, setName1] = useState("");
  const [name2, setName2] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name1.trim() || !name2.trim()) return toast.error("Please enter both names.");
    setLoading(true);
    try {
      const { data } = await api.post("/tools/love", { name1, name2 });
      setResult(data);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const share = async () => {
    const text = `${result.name1} ❤️ ${result.name2} = ${result.score}% match on Numoraa! ${result.message}`;
    try {
      if (navigator.share) await navigator.share({ title: "Numoraa Love Match", text });
      else { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard!"); }
    } catch { /* cancelled */ }
  };

  return (
    <ToolShell slug="love-calculator" name="Love Calculator" tagline="A playful compatibility spark">
      <SEO title="Love Calculator" description="A fun numerology love compatibility calculator. Enter two names for a playful compatibility score. For entertainment only." path="/tools/love-calculator" />

      <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2 max-w-2xl rounded-3xl border border-border bg-card p-7 shadow-soft" data-testid="love-form">
        <Field label="Person 1"><input className={inputCls} value={name1} onChange={(e) => setName1(e.target.value)} placeholder="Name" data-testid="love-name1" /></Field>
        <Field label="Person 2"><input className={inputCls} value={name2} onChange={(e) => setName2(e.target.value)} placeholder="Name" data-testid="love-name2" /></Field>
        <div className="sm:col-span-2">
          <button disabled={loading} data-testid="love-submit"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4 text-gold" />}
            {loading ? "Calculating…" : "Calculate love"}
          </button>
        </div>
      </form>

      {result && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="mt-14 max-w-2xl" data-testid="love-result">
          <div className="rounded-3xl bg-primary text-primary-foreground p-10 text-center">
            <p className="font-serif text-2xl">{result.name1} <span className="text-gold">&</span> {result.name2}</p>
            <div className="relative mx-auto mt-6 flex h-44 w-44 items-center justify-center">
              <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6" />
                <motion.circle cx="50" cy="50" r="45" fill="none" stroke="#D4AF37" strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={283} initial={{ strokeDashoffset: 283 }} animate={{ strokeDashoffset: 283 - (283 * result.score) / 100 }} transition={{ duration: 1.2, ease: "easeOut" }} />
              </svg>
              <div><p className="font-serif text-5xl">{result.score}%</p></div>
            </div>
            <p className="mt-6 text-lg font-light leading-relaxed">{result.message}</p>
            <button onClick={share} data-testid="love-share" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm transition-colors hover:bg-white/20">
              <Share2 className="h-4 w-4" /> Share result
            </button>
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground italic">This feature is for entertainment purposes only. ✨</p>
        </motion.div>
      )}

      <section className="mt-20 max-w-3xl" data-testid="love-explainer">
        <h2 className="font-serif text-3xl">About the love calculator</h2>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          The Numoraa love calculator is a playful tool. Enter two names and you get a compatibility score and a short message that you can share with a friend or partner.
        </p>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Please treat the score as fun, not as advice. A percentage cannot tell you whether a relationship will work. Real compatibility comes from honest communication, respect and shared values.
        </p>

        <h2 className="mt-12 font-serif text-3xl">How to use it</h2>
        <ul className="mt-4 space-y-2 text-muted-foreground leading-relaxed">
          <li>Type the first name in the Person 1 box.</li>
          <li>Type the second name in the Person 2 box.</li>
          <li>Tap Calculate love to see the score and message.</li>
          <li>Use Share result to copy or send it.</li>
        </ul>

        <h2 className="mt-12 font-serif text-3xl">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          <div>
            <h3 className="font-serif text-xl">Is the love calculator accurate?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              It is made for entertainment, so it is not a real measure of compatibility. Enjoy it, but don't base decisions on it.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Is it free?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">Yes. You can use the love calculator for free.</p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Are the names I enter saved?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Please read our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> to see how your details are handled.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl">Want a deeper numerology reading?</h3>
            <p className="mt-2 text-muted-foreground leading-relaxed">
              Try the <Link to="/tools/numerology-calculator" className="text-primary underline">numerology calculator</Link> to find your Mulank and Bhagyank, and read{" "}
              <Link to="/blog/mulank-vs-bhagyank-what-is-the-difference" className="text-primary underline">Mulank vs Bhagyank</Link> to see how they differ.
            </p>
          </div>
        </div>
      </section>
    </ToolShell>
  );
}